import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlagIcon from '@mui/icons-material/Flag';
import { directDocumentQuery, getSOWInsightsForPrepare } from '../../utils/aiService';
import { getBusinessRequirements, saveBusinessRequirements, fetchBusinessRequirementsFromSOW } from '../../utils/preparePhaseService';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

// Priority color mapping
const priorityColors = {
  high: 'error',
  medium: 'warning',
  low: 'success'
};

const BusinessRequirementsPage = () => {
  const { projectId } = useParams();
  const { project, refreshOverview } = useOutletContext();
  
  const [requirements, setRequirements] = useState({
    overview: '',
    requirements: {
      functional: [],
      nonFunctional: [],
      technical: []
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [primarySource, setPrimarySource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingRequirements, setEditingRequirements] = useState({
    overview: '',
    requirements: {
      functional: [],
      nonFunctional: [],
      technical: []
    }
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({
    id: '',
    description: '',
    priority: 'medium',
    details: '',
    notes: ''
  });
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load business requirements from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadBusinessRequirementsFromDB();
    }
  }, [projectId]);
  
  // Load business requirements from database
  const loadBusinessRequirementsFromDB = async () => {
    setLoading(true);
    try {
      const requirementsData = await getBusinessRequirements(projectId);
      
      if (requirementsData && requirementsData.overview !== undefined) {
        // We have data in the database
        setRequirements(requirementsData);
        setEditingRequirements(requirementsData);
      } else {
        // No data in database, set empty state
        const emptyRequirements = {
          overview: '',
          requirements: {
            functional: [],
            nonFunctional: [],
            technical: []
          }
        };
        setRequirements(emptyRequirements);
        setEditingRequirements(emptyRequirements);
      }
    } catch (error) {
      console.error('Error loading business requirements from database:', error);
      // Set empty state on error
      const emptyRequirements = {
        overview: '',
        requirements: {
          functional: [],
          nonFunctional: [],
          technical: []
        }
      };
      setRequirements(emptyRequirements);
      setEditingRequirements(emptyRequirements);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch requirements from project documents and save to database
  const fetchRequirementsFromSOW = async () => {
    if (!projectId) return;
    
    // Check if we have a SOW document
    const sowDoc = getSowDocument();
    if (!sowDoc) {
      setSnackbar({
        open: true,
        message: 'No project documents found. Please upload project documents first.',
        severity: 'warning'
      });
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      // Call the new backend endpoint to fetch from project documents and save to DB
      const result = await fetchBusinessRequirementsFromSOW(projectId);
      
      if (result && result.data) {
        // Update the requirements state with the fetched and saved data
        setRequirements(result.data);
        setEditingRequirements(result.data);
        
        // Store source information
        if (result.source) {
          setPrimarySource(result.source);
        }
        
        setSnackbar({
          open: true,
          message: 'Project requirements successfully fetched from project documents and saved.',
          severity: 'success'
        });
        
        // Automatically enter edit mode to allow user to review and modify
        setEditMode(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to extract project requirements from project documents.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error fetching requirements from project documents:", error);
      setSnackbar({
        open: true,
        message: 'Error fetching requirements from project documents.',
        severity: 'error'
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  // Get the SOW document from the project
  const getSowDocument = () => {
    if (project && project.documents && project.documents.sow && project.documents.sow.length > 0) {
      return project.documents.sow[0];
    }
    return null;
  };
  
  // Enter edit mode
  const handleEdit = () => {
    setEditingRequirements({...requirements});
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    setEditingRequirements({...requirements});
    setEditMode(false);
  };
  
  // Save changes to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveBusinessRequirements(projectId, editingRequirements);
      
      setRequirements({...editingRequirements});
      setEditMode(false);
      if (refreshOverview) refreshOverview();
      
      setSnackbar({
        open: true,
        message: 'Project requirements saved successfully.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving business requirements:', error);
      setSnackbar({
        open: true,
        message: 'Error saving project requirements. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle overview change
  const handleOverviewChange = (e) => {
    setEditingRequirements(prevState => ({
      ...prevState,
      overview: e.target.value
    }));
  };
  
  // Open add/edit dialog
  const handleOpenDialog = (type, index = null) => {
    setDialogType(type);
    setDialogItemIndex(index);
    
    if (index !== null) {
      // Edit existing item
      switch (type) {
        case 'functional':
          setDialogItem({...editingRequirements.requirements.functional[index]});
          break;
        case 'nonFunctional':
          setDialogItem({...editingRequirements.requirements.nonFunctional[index]});
          break;
        case 'technical':
          setDialogItem({...editingRequirements.requirements.technical[index]});
          break;
        default:
          setDialogItem({
            id: '',
            description: '',
            priority: 'medium',
            details: '',
            notes: ''
          });
      }
    } else {
      // Add new item - generate a new ID based on type
      const newId = generateNewId(type);
      
      setDialogItem({
        id: newId,
        description: '',
        priority: 'medium',
        details: '',
        notes: ''
      });
    }
    
    setOpenDialog(true);
  };
  
  // Generate a new ID based on type
  const generateNewId = (type) => {
    let prefix = 'REQ';
    let existingIds = [];

    switch (type) {
      case 'functional':
        prefix = 'PR';
        existingIds = editingRequirements.requirements.functional.map(item => item.id);
        break;
      case 'nonFunctional':
        prefix = 'NFR';
        existingIds = editingRequirements.requirements.nonFunctional.map(item => item.id);
        break;
      case 'technical':
        prefix = 'TR';
        existingIds = editingRequirements.requirements.technical.map(item => item.id);
        break;
      default:
        prefix = 'REQ';
    }
    
    // Find the highest existing ID number and increment by 1
    let highestNum = 0;
    existingIds.forEach(id => {
      const match = id.match(new RegExp(`^${prefix}-(\\d+)$`));
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > highestNum) highestNum = num;
      }
    });
    
    // Format with leading zeros
    const nextNum = highestNum + 1;
    const paddedNum = nextNum.toString().padStart(3, '0');
    
    return `${prefix}-${paddedNum}`;
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogItem({
      id: '',
      description: '',
      priority: 'medium',
      details: '',
      notes: ''
    });
  };
  
  // Handle dialog input change
  const handleDialogInputChange = (e) => {
    const { name, value } = e.target;
    setDialogItem(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Save dialog item
  const handleSaveDialogItem = () => {
    if (!dialogItem.description.trim()) {
      return;
    }
    
    const updatedRequirements = {...editingRequirements};
    
    if (dialogItemIndex === null) {
      // Add new item
      switch (dialogType) {
        case 'functional':
          updatedRequirements.requirements.functional = [...updatedRequirements.requirements.functional, {...dialogItem}];
          break;
        case 'nonFunctional':
          updatedRequirements.requirements.nonFunctional = [...updatedRequirements.requirements.nonFunctional, {...dialogItem}];
          break;
        case 'technical':
          updatedRequirements.requirements.technical = [...updatedRequirements.requirements.technical, {...dialogItem}];
          break;
        default:
          break;
      }
    } else {
      // Edit existing item
      switch (dialogType) {
        case 'functional':
          updatedRequirements.requirements.functional[dialogItemIndex] = {...dialogItem};
          break;
        case 'nonFunctional':
          updatedRequirements.requirements.nonFunctional[dialogItemIndex] = {...dialogItem};
          break;
        case 'technical':
          updatedRequirements.requirements.technical[dialogItemIndex] = {...dialogItem};
          break;
        default:
          break;
      }
    }
    
    setEditingRequirements(updatedRequirements);
    handleCloseDialog();
  };
  
  // Delete item
  const handleDeleteItem = (type, index) => {
    const updatedRequirements = {...editingRequirements};
    
    switch (type) {
      case 'functional':
        updatedRequirements.requirements.functional = updatedRequirements.requirements.functional.filter((_, i) => i !== index);
        break;
      case 'nonFunctional':
        updatedRequirements.requirements.nonFunctional = updatedRequirements.requirements.nonFunctional.filter((_, i) => i !== index);
        break;
      case 'technical':
        updatedRequirements.requirements.technical = updatedRequirements.requirements.technical.filter((_, i) => i !== index);
        break;
      default:
        break;
    }
    
    setEditingRequirements(updatedRequirements);
  };
  
  // Render a list of requirements
  const renderRequirementsList = (items, type) => {
    return (
      <List>
        {items.map((item, index) => (
          <ListItem
            key={index}
            alignItems="flex-start"
            sx={{ 
              mb: 1, 
              backgroundColor: 'action.hover', 
              borderRadius: 1,
              flexDirection: 'column'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              width: '100%', 
              justifyContent: 'space-between',
              alignItems: 'flex-start' 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip 
                  label={item.id} 
                  color="primary" 
                  size="small"
                  sx={{ mr: 1, fontWeight: 'bold' }}
                />
                <Typography variant="subtitle1" color="text.primary">
                  {item.description}
                </Typography>
              </Box>
              <Box>
                <Chip 
                  icon={<FlagIcon />}
                  label={item.priority.toUpperCase()} 
                  color={priorityColors[item.priority]} 
                  size="small"
                  sx={{ mr: 1 }}
                />
                {editMode && (
                  <>
                    <IconButton size="small" onClick={() => handleOpenDialog(type, index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteItem(type, index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
            
            {/* Details section */}
            {(item.details || item.notes) && (
              <Box sx={{ pl: 7, mt: 1, width: '100%' }}>
                {item.details && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Details:</strong> {item.details}
                  </Typography>
                )}
                {item.notes && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Notes:</strong> {item.notes}
                  </Typography>
                )}
              </Box>
            )}
          </ListItem>
        ))}
        {editMode && (
          <ListItem>
            <Button
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(type)}
              variant="outlined"
              size="small"
            >
              Add Requirement
            </Button>
          </ListItem>
        )}
      </List>
    );
  };

  const hasExistingRequirements =
    Boolean(requirements.overview) ||
    requirements.requirements.functional.length > 0 ||
    requirements.requirements.nonFunctional.length > 0 ||
    requirements.requirements.technical.length > 0;
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle variant="h5">
            <AssignmentIcon />
            Project Requirements
          </SectionTitle>
          <Box>
            {editMode ? (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    hasExistingRequirements ? setFetchConfirmOpen(true) : fetchRequirementsFromSOW();
                  }}
                  disabled={loading || aiLoading || !getSowDocument()}
                  sx={{ mr: 1 }}
                >
                  {loading
                    ? 'Loading...'
                    : aiLoading
                    ? hasExistingRequirements ? 'Regenerating...' : 'Fetching...'
                    : hasExistingRequirements ? 'Regenerate from Project Documents' : 'Fetch from Project Documents'}
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" paragraph>
          Document the project requirements that the SAP implementation must fulfill based on the stakeholder needs and business processes.
        </Typography>
        
        {!getSowDocument() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No project documents found. Upload project documents to enable AI-assisted content generation.
          </Alert>
        )}
        
        {primarySource && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Content generated from: {primarySource.filename || primarySource.document_name || 'Project Document'}
          </Alert>
        )}
        
        {aiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Introduction */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Introduction
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={editingRequirements.overview}
                    onChange={handleOverviewChange}
                    placeholder="Enter an introduction to the project requirements"
                  />
                ) : (
                  <Typography variant="body1" paragraph>
                    {requirements.overview}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* Project Requirements */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Project Requirements
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {renderRequirementsList(
                  editMode ? editingRequirements.requirements.functional : requirements.requirements.functional, 
                  'functional'
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* AI Response Accordion (if available) */}
      {aiResponse && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>AI-Generated Content (Raw Response)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {aiResponse}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex === null ? 'Add' : 'Edit'} Project Requirement
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                name="id"
                label="Requirement ID"
                value={dialogItem.id}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="priority-label">Priority</InputLabel>
                <Select
                  labelId="priority-label"
                  name="priority"
                  value={dialogItem.priority}
                  onChange={handleDialogInputChange}
                  label="Priority"
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={dialogItem.description}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="details"
                label="Details"
                value={dialogItem.details}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={dialogItem.notes}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveDialogItem} 
            variant="contained" 
            disabled={!dialogItem.description || !dialogItem.id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <SafetyConfirmDialog
        open={fetchConfirmOpen}
        title={hasExistingRequirements ? 'Regenerate Project Requirements from Project Documents?' : 'Fetch Project Requirements from Project Documents?'}
        warning={hasExistingRequirements ? 'The current Project Requirements will be replaced.' : 'Project Requirements will be generated from Project Documents.'}
        paragraphs={[
          hasExistingRequirements
            ? 'This action will use AI to regenerate the current project requirements and supporting details.'
            : 'This action will use AI to fetch project requirements and supporting details from Project Documents.',
          ...(hasExistingRequirements ? ['Any manual edits made to the existing Project Requirements will be permanently lost.'] : [])
        ]}
        confirmLabel={hasExistingRequirements ? 'Yes, Regenerate Requirements' : 'Yes, Fetch Requirements'}
        loading={aiLoading}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={() => { setFetchConfirmOpen(false); fetchRequirementsFromSOW(); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessRequirementsPage;
